import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import {
    View,
    StyleSheet,
    Text,
    TouchableOpacity,
    ActivityIndicator,
    Pressable,
    Linking,
    Image,
} from "react-native"
import { useFocusEffect } from "@react-navigation/native"
import { connect } from "react-redux"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import FilledButtonM from "@common/components/button/FilledButtonM"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import { theme } from "@common/theme/theme"
import { apiRequest } from "@data/redux/actions/api"
import FunnelStepHeader from "@common/components/funnel/FunnelStepHeader"
import SummerEyebrow from "../summer/components/SummerEyebrow"
import SummerHelpBanner, {
    SUPPORT_PHONE_DISPLAY,
    SUPPORT_PHONE_URL,
} from "../summer/components/SummerHelpBanner"
import { subjectIconUri } from "../summer/constants/summerContent"
import { formatEuros } from "../brevet/utils/formatEuros"
import { useStageFunnel } from "./context/StageFunnelContext"
import { useStageTrack } from "./hooks/useStageTrack"
import { useStageFunnelGuard } from "./hooks/useStageFunnelGuard"
import { useStageFunnelConfig } from "./hooks/useStageFunnelConfig"
import { stageFunnelService } from "./services/stageFunnelService"
import StageScarcityMeter from "./components/StageScarcityMeter"
import StageSubjectPickerModal from "./components/StageSubjectPickerModal"
// Le contrôle « power user » de répartition des heures par matière existe déjà
// sur le reveal du programme d'été : mêmes steppers, même somme verrouillée,
// même règle de reste. On le réutilise plutôt que d'en écrire un second.
import SummerSubjectHoursModal from "../summer/components/SummerSubjectHoursModal"
import StageWeekendComposer from "./components/StageWeekendComposer"
import FunnelStepLayout, {
    bandSpacerHeight,
} from "@common/components/funnel/FunnelStepLayout"
import { useFunnelSectionScroll } from "@common/components/funnel/useFunnelSectionScroll"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { FUNNEL_SCENES } from "@common/components/funnel/funnelScenes"
import {
    getTemplateName,
    getVariantsFromConfig,
    getSeancesPerWeek,
    getSlotsFromConfig,
    showsScarcity,
    getSessionsFromConfig,
    getSeasonFirstStart,
    getPricingByCount,
    getMaxSubjectsPerSession,
    getMinPricingCents,
    getMaxSelectableSessions,
    getPreinscriptionCents,
    groupSessionsByStart,
    getSessionDurationMinutes,
    hourlyPriceCents,
    pricingForCount,
    stageSessionScarcity,
    sellsBySeance,
    discountPercent,
    distributeSlots,
    hoursFromDistribution,
} from "./constants/stageContent"
import { SUBJECT_GOAL_DEFAULT } from "@common/components/funnel/funnelSubjectGoals"

/**
 * Étape composition du funnel Stage — « Choisissez vos sessions », APRÈS le
 * diagnostic et AVANT le reveal du programme.
 *
 * Pivot UX (recette owner) : plus de cartes formule ni de badge « Recommandé »
 * ni de date de départ — le parent COCHE ses sessions (une carte par date de la
 * saison, multi-sélection, non contiguës OK, RIEN de pré-coché : l'ancrage d'un
 * prix pré-sélectionné fait fuir) et choisit les matières DE CHAQUE session
 * cochée. Le prix se construit sous ses yeux : « dès X € » avant le premier
 * coche, puis le prix du count coché (pricing_by_count).
 *
 * 100 % catalogue-driven : sessions (saison × horaire), prix par nombre de
 * sessions et cap de matières par session viennent du catalogue API
 * (GET /{stateId}). ZÉRO constante de prix/date ici. Un count sans variante en
 * vente n'est PAS proposable : les cases restantes sont grisées avec un
 * « N sessions et + : nous contacter » (l'admin ajoute la variante en BO).
 */

const StageConfiguratorScreen = (props) => {
    const { navigation, reduxFunnelStateId } = props
    const { state, update } = useStageFunnel()
    const track = useStageTrack()
    // Refresh-safe / URL-partageable (voir useStageFunnelGuard).
    const { userParam, nextParams } = useStageFunnelGuard()
    const stateId = state.funnelStateId || reduxFunnelStateId

    // Config catalogue : sessions proposées, prix par count, cap matières,
    // horaires, nom du stage.
    const {
        config,
        loading: loadingConfig,
        failed: configFailed,
        retry: retryConfig,
    } = useStageFunnelConfig(stateId)
    const templateName = getTemplateName(config) || state.templateName
    const offeredSessions = getSessionsFromConfig(config)
    const pricingByCount = getPricingByCount(config)
    const maxSubjects = getMaxSubjectsPerSession(config)
    const maxSelectable = getMaxSelectableSessions(pricingByCount)
    const slots = getSlotsFromConfig(config)
    const scarcityShown = showsScarcity(config)
    const variants = getVariantsFromConfig(config)
    // Unité de vente servie par le catalogue — jamais devinée de la forme des
    // clés, qui accepte les deux en permanence.
    const isSeanceUnit = sellsBySeance(config)

    /**
     * La clé sous laquelle les matières d'un item se rangent.
     *
     * En unité semaine, c'est l'item lui-même. En unité créneau, c'est le
     * WEEK-END : le parent ne pose pas des matières créneau par créneau, il dit
     * « du Français sur ce week-end » — et c'est ce que la composition du
     * programme et l'objectif pédagogique relisent, par session.
     */
    const subjectsKeyOf = (session) =>
        isSeanceUnit ? session.sessionStart : session.key

    // --- Matières (chargées depuis l'API selon la classe ∩ scope template) ---
    // « Voir le programme » ne se grise jamais : il EMMÈNE le parent là où il
    // manque quelque chose. Un bouton mort sans explication laisse chercher.
    const scrollRef = useRef(null)
    const { sectionRef, onSectionLayout, sectionY, scrollToSection } =
        useFunnelSectionScroll(scrollRef)
    const dimensions = useContext(DimensionsContext)
    const [sectionToFix, setSectionToFix] = useState(null)
    // Compte les tentatives bloquées : le rappel tremble à chacune, même quand
    // l'écran est déjà au bon endroit et que rien d'autre ne bouge.
    const [toFixNudge, setToFixNudge] = useState(0)

    const [availableSubjects, setAvailableSubjects] = useState([])
    const [loadingSubjects, setLoadingSubjects] = useState(false)

    // --- Sélections ---
    // sessions cochées = dates ISO de début (identité de sélection ET wire),
    // triées ; matières PAR session = { date ISO: [ids matière en STRING] }
    // (les chips comparent des strings — coercion au seed, reprise backend en
    // int). Les matières d'une session décochée sont GARDÉES en local (re-coche
    // amical) et élaguées au moment de persister/naviguer.
    const [selectedSessions, setSelectedSessions] = useState(
        (state.sessions || []).map(String)
    )
    const [sessionSubjects, setSessionSubjects] = useState(
        normalizeSessionSubjects(state.sessionSubjects)
    )
    const [slotKey, setSlotKey] = useState(state.slotKey || null)
    const [error, setError] = useState(null)
    // Clé de la session dont la modale « matières » est ouverte (ou null).
    const [subjectPickerSession, setSubjectPickerSession] = useState(null)
    // Répartition choisie à la main via « Répartir autrement » : { start:
    // { subjectId: heures } }. Absente, la répartition automatique s'applique —
    // c'est le cas de l'immense majorité des parcours.
    //
    // Elle est réhydratée depuis l'état du funnel, où la répartition
    // AUTOMATIQUE est aussi enregistrée : les deux se ressemblent, mais une
    // répartition périmée doit céder. `manualSplitFor` s'en charge.
    const [hoursOverride, setHoursOverride] = useState(
        () => state.sessionSubjectHours ?? {}
    )
    const [redistributeOpen, setRedistributeOpen] = useState(false)
    // L'objectif visé par matière, par week-end : { start: { subjectId: slug } }.
    const [subjectGoals, setSubjectGoals] = useState(
        () => state.sessionSubjectGoals ?? {}
    )

    const setGoalFor = (start, subjectId, slug) =>
        setSubjectGoals((prev) => ({
            ...prev,
            [start]: { ...(prev[start] ?? {}), [String(subjectId)]: slug },
        }))

    // Re-sème les sélections locales depuis le contexte à chaque focus (back-nav /
    // reprise via lien magique) — le contexte est la source de vérité.
    useFocusEffect(
        useCallback(() => {
            if (state.sessions?.length) {
                setSelectedSessions(state.sessions.map(String))
            }
            if (
                state.sessionSubjects &&
                Object.keys(state.sessionSubjects).length
            ) {
                setSessionSubjects(
                    normalizeSessionSubjects(state.sessionSubjects)
                )
            }
            if (state.slotKey) setSlotKey(state.slotKey)
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [state.sessions, state.sessionSubjects, state.slotKey])
    )

    useEffect(() => {
        track("stage_funnel_configurator_viewed")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Sans classe → impossible de proposer des matières → on renvoie au début.
    useEffect(() => {
        if (!state.studentClass && !userParam) {
            navigation.navigate("stage/start")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Sessions persistées re-validées contre le catalogue VIVANT : une reprise
    // tardive peut porter une date qui n'est plus offerte (saison avancée) OU
    // une semaine devenue « Complet » (entrée dans son délai plancher 48h) —
    // on l'écarte silencieusement, le backend re-refuserait au checkout (422).
    useEffect(() => {
        if (!offeredSessions.length) return
        // Cochable = offerte ET non « Complet ».
        const bookable = new Set(
            offeredSessions.filter((s) => !s.full).map((s) => s.key)
        )
        setSelectedSessions((prev) => {
            const next = prev.filter((d) => bookable.has(d))
            return next.length === prev.length ? prev : next
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        offeredSessions
            .map((s) => `${s.startDate}:${s.full ? 1 : 0}`)
            .join(","),
    ])

    // Cap matières re-tronqué quand le cap servi est plus strict que la
    // sélection reprise (ex. classe corrigée au BO : 2 matières persistées,
    // cap devenu 1) — le backend re-valide ce cap au checkout (422).
    useEffect(() => {
        if (!config) return
        setSessionSubjects((prev) => {
            let changed = false
            const next = {}
            Object.entries(prev).forEach(([date, ids]) => {
                next[date] =
                    ids.length > maxSubjects ? ids.slice(0, maxSubjects) : ids
                if (next[date] !== ids) changed = true
            })
            return changed ? next : prev
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxSubjects, !!config])

    // Horaire : auto-sélection silencieuse (premier horaire) — la section n'est
    // VISIBLE que s'il y a un vrai choix (> 1 horaire au template).
    useEffect(() => {
        if (!slots.length) return
        if (!slotKey || !slots.some((s) => s.key === slotKey)) {
            setSlotKey(slots[0].key)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slots.map((s) => s.key).join(",")])

    // Récupère les matières proposables (classe ∩ scope template, triées).
    useEffect(() => {
        if (!state.studentClass) return
        let cancelled = false
        setLoadingSubjects(true)
        stageFunnelService
            .getStageSubjects({
                studentClass: state.studentClass,
                templateSlug: state.templateSlug,
            })
            .then((res) => {
                if (cancelled) return
                const list = res?.data?.data ?? res?.data ?? []
                const fetched = Array.isArray(list) ? list : []
                setAvailableSubjects(fetched)
                // Filtre les matières persistées sur celles réellement proposées —
                // UNIQUEMENT contre un catalogue non vide (un fetch transitoirement
                // vide ne doit jamais effacer les matières reprises).
                if (fetched.length) {
                    const allowedIds = new Set(fetched.map((s) => String(s.id)))
                    setSessionSubjects((prev) =>
                        Object.fromEntries(
                            Object.entries(prev).map(([date, ids]) => [
                                date,
                                ids.filter((id) => allowedIds.has(id)),
                            ])
                        )
                    )
                }
            })
            .catch(() => {
                if (!cancelled) setAvailableSubjects([])
            })
            .finally(() => {
                if (!cancelled) setLoadingSubjects(false)
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.studentClass, state.templateSlug])

    const count = selectedSessions.length
    const pricing = pricingForCount(pricingByCount, count)
    const minPricingCents = getMinPricingCents(pricingByCount)
    // Remise CALCULÉE des deux montants — jamais servie par l'API, sans quoi
    // un pourcentage stocké finirait par contredire les prix qu'il accompagne.
    // Null quand la formule n'a pas de prix de référence : l'affichage est
    // alors exactement celui d'avant.
    const discount = pricing
        ? discountPercent(pricing.priceCents, pricing.compareAtCents)
        : null
    // Durée d'un créneau, pour dire « 6 h » plutôt que « 3 créneaux ».
    const sessionHours = (getSessionDurationMinutes(config) || 120) / 60

    // Items groupés par session — en unité semaine chaque groupe ne contient
    // que lui-même, donc la même structure sert les deux modes.
    const sessionGroups = groupSessionsByStart(offeredSessions)

    // UN SEUL week-end est proposé : le prochain dont au moins un créneau est
    // encore vendable. Montrer celui dans trois semaines allongerait une
    // décision qu'on veut immédiate — le parent qui en veut plus reviendra.
    const openGroup = sessionGroups.find((g) =>
        g.items.some((item) => item.full !== true)
    )
    // Celui qui vient de fermer, s'il précède le week-end proposé : il reste
    // affiché en une ligne barrée pour expliquer pourquoi on propose le
    // suivant, au lieu de disparaître sans un mot.
    const closedGroup = openGroup
        ? (sessionGroups
              .slice(0, sessionGroups.indexOf(openGroup))
              .filter((g) => g.items.every((item) => item.full === true))
              .slice(-1)[0] ?? null)
        : null
    // La fermeture du week-end = celle de son premier créneau encore ouvert.
    const openGroupClosesAt =
        openGroup?.items.find((item) => item.full !== true)?.closesAt ?? null

    // Les matières et objectifs vivent sur le week-end proposé.
    const openStart = openGroup?.start ?? null
    const openSubjectIds = openStart ? (sessionSubjects[openStart] ?? []) : []
    const openSelectedCount = openGroup
        ? openGroup.items.filter((i) => selectedSessions.includes(i.key)).length
        : 0
    // La répartition des créneaux entre matières : automatique, sauf si le
    // parent est allé la reprendre à la main.
    const openOverride = manualSplitFor(
        hoursOverride[openStart],
        openSubjectIds,
        openSelectedCount * sessionHours
    )
    const openHoursBySubject =
        openOverride ??
        hoursFromDistribution(
            distributeSlots(openSubjectIds, openSelectedCount),
            sessionHours
        )

    // Cocher une session de plus n'est possible que si le count suivant est
    // couvert par une variante en vente — sinon les cases restantes grisent.
    const canAddMore = !!pricingForCount(pricingByCount, count + 1)

    // Semaines « Complet » (délai plancher atteint) : non cochables. Garde-fou
    // en plus de `disabled` sur la carte — un clic sur une semaine complète est
    // inerte, jamais ajouté à la sélection.
    // Items « Complet » (délai plancher atteint) : non cochables. Garde-fou
    // en plus de `disabled` sur la carte — un clic sur un item complet est
    // inerte, jamais ajouté à la sélection.
    const fullSessionKeys = new Set(
        offeredSessions.filter((s) => s.full).map((s) => s.key)
    )

    // L'identité de sélection est la CLÉ de l'item, pas sa date : en unité
    // semaine les deux coïncident (comportement inchangé), en unité créneau la
    // clé porte l'heure — deux créneaux du même samedi doivent se distinguer.
    const toggleSession = (sessionKey) => {
        if (fullSessionKeys.has(sessionKey)) return
        setError(null)
        setSelectedSessions((prev) => {
            if (prev.includes(sessionKey)) {
                return prev.filter((k) => k !== sessionKey)
            }
            if (!pricingForCount(pricingByCount, prev.length + 1)) return prev
            return [...prev, sessionKey].sort()
        })
    }

    const toggleSubjectForSession = (startDate, id) => {
        setError(null)
        setSessionSubjects((prev) => {
            const current = prev[startDate] || []
            if (current.includes(id)) {
                return {
                    ...prev,
                    [startDate]: current.filter((s) => s !== id),
                }
            }
            // Plafond PAR session (cap servi par le backend depuis la classe) :
            // au plafond, les chips restantes de la session sont grisées.
            if (current.length >= maxSubjects) return prev
            return { ...prev, [startDate]: [...current, id] }
        })
    }

    // Matières élaguées aux seules sessions cochées — c'est CE shape qui part
    // en contexte et sur le wire (answers / program / checkout).
    // Les matières se rangent PAR SESSION : les clés à conserver sont donc
    // celles des sessions touchées par la sélection, pas celles des items.
    // En unité semaine les deux ensembles coïncident.
    const selectedSubjectKeys = new Set(
        offeredSessions
            .filter((s) => selectedSessions.includes(s.key))
            .map(subjectsKeyOf)
    )
    const prunedSessionSubjects = Object.fromEntries(
        [...selectedSubjectKeys].map((k) => [k, sessionSubjects[k] || []])
    )
    const sessionsMissingSubjects = [...selectedSubjectKeys].filter(
        (k) => !(sessionSubjects[k] || []).length
    )

    const selectedSlot = slots.find((s) => s.key === slotKey) || null

    // Ce qui manque, dans l'ordre où le parent doit le traiter — une seule chose
    // à la fois. `null` : rien ne bloque. Catalogue absent ou en erreur n'y
    // figure pas : là, le CTA est déjà désactivé.
    const blocker = useMemo(() => {
        // Unité SEMAINE : pas de compositeur, donc pas de section vers laquelle
        // emmener le parent — cette liste-là garde son message d'erreur.
        if (!isSeanceUnit) return null
        if (!openSubjectIds.length) return "subjects"
        if (!openSelectedCount) return "slots"
        // Un créneau accueille UNE matière : moins de créneaux que de matières
        // en laisserait une à zéro heure, sans le dire.
        if (openSelectedCount < openSubjectIds.length) return "slots"
        return null
    }, [isSeanceUnit, openSubjectIds.length, openSelectedCount])

    // Le blocage levé, la mise en avant s'efface : elle ne survit pas au geste
    // qui la résout.
    useEffect(() => {
        if (!blocker) setSectionToFix(null)
        else if (sectionToFix && sectionToFix !== blocker)
            setSectionToFix(blocker)
        setToFixNudge((n) => n + 1)
    }, [blocker, sectionToFix])

    const onContinue = () => {
        if (blocker) {
            // Le parent a VOULU avancer et n'a pas pu : sans cet event, ce refus
            // est indistinguable d'un abandon.
            track("stage_funnel_configuration_blocked", {
                funnel_state_id: stateId,
                blocker,
                subjects_count: openSubjectIds.length,
                slots_count: openSelectedCount,
            })
            setSectionToFix(blocker)
            setToFixNudge((n) => n + 1)
            // La mesure de repli se compose de l'espaceur du bandeau (au-dessus
            // de la feuille sur petit écran) et du compositeur dans la feuille.
            scrollToSection(
                blocker,
                bandSpacerHeight(dimensions?.width ?? 0) +
                    (sectionY.current.composer ?? 0)
            )
            return
        }
        if (!count) {
            setError("no_sessions")
            return
        }
        // Le CTA exige ≥ 1 session ET chaque session cochée avec ≥ 1 matière.
        if (sessionsMissingSubjects.length) {
            setError("missing_subjects")
            return
        }
        // Count non couvert (trou de catalogue en cours de reprise) : combo
        // invendable — le checkout le refuserait en 422, on le dit ici.
        if (!pricing) {
            setError("count_not_sellable")
            return
        }
        // Stampe les NOMS des matières retenues (id → nom) depuis le catalogue
        // déjà chargé ici — le récap du checkout les affiche instantanément,
        // sans re-fetch (plus de matière qui « pop » sous la session).
        const nameById = new Map(
            availableSubjects.map((s) => [String(s.id), s.name])
        )
        const subjectNames = {}
        Object.values(prunedSessionSubjects).forEach((ids) => {
            ;(ids || []).forEach((id) => {
                const name = nameById.get(String(id))
                if (name) subjectNames[String(id)] = name
            })
        })
        // Les volumes ne voyagent qu'en unité créneau — en unité semaine il n'y
        // en a pas, et envoyer un objet vide ajouterait du bruit au contrat.
        // Les volumes ne voyagent qu'en unité créneau, et ils sont DÉRIVÉS de la
        // répartition automatique — le contrat backend ne change pas, c'est la
        // saisie qui disparaît. Idem pour les objectifs : ceux des matières que
        // le parent a effectivement gardées, avec le cran par défaut pour celles
        // qu'il n'a pas touchées.
        const prunedSubjectHours = isSeanceUnit
            ? Object.fromEntries(
                  [...selectedSubjectKeys].map((k) => {
                      const picked = (offeredSessions ?? []).filter(
                          (i) =>
                              (i.sessionStart ?? i.startDate) === k &&
                              selectedSessions.includes(i.key)
                      ).length
                      const manual = manualSplitFor(
                          hoursOverride[k],
                          prunedSessionSubjects[k] ?? [],
                          picked * sessionHours
                      )
                      return [
                          k,
                          manual
                              ? manual
                              : hoursFromDistribution(
                                    distributeSlots(
                                        prunedSessionSubjects[k] ?? [],
                                        picked
                                    ),
                                    sessionHours
                                ),
                      ]
                  })
              )
            : null
        const prunedSubjectGoals = isSeanceUnit
            ? Object.fromEntries(
                  [...selectedSubjectKeys].map((k) => [
                      k,
                      Object.fromEntries(
                          (prunedSessionSubjects[k] ?? []).map((id) => [
                              String(id),
                              subjectGoals[k]?.[String(id)] ??
                                  SUBJECT_GOAL_DEFAULT,
                          ])
                      ),
                  ])
              )
            : null
        // L'objectif macro n'est plus demandé au diagnostic : il se DÉDUIT des
        // curseurs par matière, qui disent la même chose en plus précis. Viser
        // « Ambitieux » ou « Expert » quelque part, c'est prendre de l'avance —
        // et c'est ce que la composition lit pour aller chercher des chapitres
        // au-delà de ce qui est vu en classe (deriveAdvanceFlag).
        const aimsHigh = Object.values(prunedSubjectGoals ?? {}).some(
            (perSubject) =>
                Object.values(perSubject ?? {}).some((slug) =>
                    ["ambitieux", "expert"].includes(slug)
                )
        )
        const derivedObjective = aimsHigh ? "prendre_avance" : "combler_lacunes"

        update({
            objective: derivedObjective,
            sessions: selectedSessions,
            sessionSubjects: prunedSessionSubjects,
            ...(prunedSubjectHours
                ? { sessionSubjectHours: prunedSubjectHours }
                : {}),
            ...(prunedSubjectGoals
                ? { sessionSubjectGoals: prunedSubjectGoals }
                : {}),
            subjectNames,
            slotKey: slotKey || null,
        })
        if (stateId) {
            props.apiRequest(
                stageFunnelService.updateStageAnswers,
                { stateId },
                {
                    objective: derivedObjective,
                    sessions: selectedSessions,
                    sessionSubjects: prunedSessionSubjects,
                    ...(prunedSubjectHours
                        ? { sessionSubjectHours: prunedSubjectHours }
                        : {}),
                    ...(prunedSubjectGoals
                        ? { sessionSubjectGoals: prunedSubjectGoals }
                        : {}),
                    slotKey: slotKey || undefined,
                }
            )
        }
        track("stage_funnel_configurator_completed", {
            funnel_state_id: stateId,
            nb_sessions: count,
            sessions: selectedSessions,
            nb_subjects_total: [...selectedSubjectKeys].reduce(
                (acc, k) => acc + (sessionSubjects[k]?.length || 0),
                0
            ),
            slot_key: slotKey,
        })
        // Reveal du programme — généré sur les semaines cochées.
        navigation.navigate("stage/plan", nextParams)
    }

    const subjectOptions = availableSubjects.map((s) => ({
        id: String(s.id),
        label: s.name,
        iconUri: subjectIconUri(s.icon_path),
    }))
    // Résolution id → option (nom + icône) pour le résumé compact des matières
    // choisies sur la carte.
    const subjectById = new Map(subjectOptions.map((opt) => [opt.id, opt]))

    // Ancre de l'index de rareté = début ABSOLU de saison, servi par le backend
    // (inclut les semaines déjà démarrées/masquées). Référence FIXE, identique à
    // la LP : « Semaine 1 » ne re-numérote pas quand les 1ʳᵉˢ semaines sortent de
    // la liste. Fallback : la 1ʳᵉ session encore offerte (config legacy sans le
    // champ).
    const seasonFirstStart =
        getSeasonFirstStart(config) ?? offeredSessions[0]?.startDate ?? null
    const subjectsLoading = loadingSubjects && availableSubjects.length === 0

    // Applique la sélection de la modale à la session éditée — ou à TOUTES les
    // sessions cochées quand « Appliquer à toutes » est coché.
    const applySubjectsFromPicker = (ids, { applyToAll } = {}) => {
        const date = subjectPickerSession
        if (!date) return
        const capped = (ids || []).slice(0, maxSubjects)
        setError(null)
        setSessionSubjects((prev) => {
            if (applyToAll) {
                const next = { ...prev }
                // « Appliquer à toutes » vise les SESSIONS cochées, pas les
                // items : en unité créneau, quatre créneaux d'un même week-end
                // ne portent qu'une entrée de matières.
                selectedSubjectKeys.forEach((k) => {
                    next[k] = capped
                })
                return next
            }
            return { ...prev, [date]: capped }
        })
        setSubjectPickerSession(null)
    }

    // Catalogue vide une fois chargé = saison hors vente (aucune session à
    // venir / aucune variante dans sa fenêtre de vente) → pas de vente.
    const catalogUnavailable =
        !loadingConfig &&
        !!config &&
        (!offeredSessions.length || !maxSelectable)

    // Config PAS chargée (réseau, 4xx/5xx) ≠ catalogue hors vente : c'est une
    // erreur visible avec retry — jamais un configurateur mort qui laisse le
    // parent devant des sections vides sans explication.
    const configLoadError =
        !loadingConfig && (configFailed || (!config && !!stateId))

    // « soit X €/heure » : division d'affichage du total catalogue par le
    // volume horaire réel — sessions cochées × séances/sem (rythme de la
    // variante du count, sinon l'horaire choisi) × durée de séance (les
    // séances font 2 h : un prix « par séance » serait trompeur). Jamais un
    // prix recalculé.
    const pricingVariant =
        variants.find((v) => v.key === pricing?.variantKey) || null
    const seancesPerWeek =
        pricingVariant?.sessionsPerWeek ||
        selectedSlot?.sessions?.length ||
        null
    // Avant tout choix, le rythme se lit sur l'horaire du catalogue.
    const weeklySeances = seancesPerWeek || getSeancesPerWeek(config)
    // En unité créneau, `count` compte DÉJÀ des séances : passer le rythme
    // hebdomadaire de la variante multiplierait les heures par ce rythme et
    // afficherait un tarif horaire trois fois trop bas — sur l'écran même qui
    // doit justifier le tarif du régulier.
    const perHourCents = hourlyPriceCents({
        priceCents: pricing?.priceCents,
        sessionsCount: count,
        seancesPerWeek: isSeanceUnit ? 1 : seancesPerWeek,
        sessionDurationMinutes: getSessionDurationMinutes(config),
    })

    // Ce qui reste à cocher SUR L'OFFRE PROPOSÉE : en unité créneau, le
    // week-end affiché — pas les créneaux des week-ends suivants, qu'on ne
    // vend pas ici. Sinon « 5 créneaux et + » s'affichait alors que les quatre
    // du week-end étaient cochés et qu'il n'y avait rien de plus à vendre.
    const hasUncheckedLeft = isSeanceUnit
        ? !!openGroup &&
          openGroup.items.some(
              (item) =>
                  item.full !== true && !selectedSessions.includes(item.key)
          )
        : count < offeredSessions.length

    return (
        <FunnelStepLayout
            scene={FUNNEL_SCENES.composition}
            scrollRef={scrollRef}
            contentContainerStyle={styles.content}
            header={
                <FunnelStepHeader
                    step={3}
                    total={6}
                    onBack={() => navigation.goBack()}
                    // Première étape d'un abonné reconnu : rien derrière.
                    hideBack={state.profileKnown}
                />
            }
            footer={
                <View style={styles.footer}>
                    {/* Le prix voyage AVEC le bouton : le parent voit ce qu'il
                        achète au moment où il décide, sans remonter la page. */}
                    {isSeanceUnit && count > 0 && pricing ? (
                        <View style={styles.footerRecap}>
                            <Text style={styles.footerRecapLine}>
                                {count} créneau{count > 1 ? "x" : ""} ·{" "}
                                {count * sessionHours} h
                            </Text>
                            <View style={styles.footerPriceRow}>
                                {discount != null ? (
                                    <Text style={styles.footerCompare}>
                                        {formatEuros(
                                            pricing.compareAtCents / 100
                                        )}
                                    </Text>
                                ) : null}
                                <Text style={styles.footerPrice}>
                                    {formatEuros(pricing.priceCents / 100)}
                                </Text>
                            </View>
                        </View>
                    ) : null}
                    <FilledButtonM
                        label="Voir le programme"
                        onPress={onContinue}
                        disabled={catalogUnavailable || configLoadError}
                        color={
                            catalogUnavailable || configLoadError
                                ? theme.colors.lightMore
                                : theme.colors.primary
                        }
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            }
            overlay={
                <>
                    <SummerSubjectHoursModal
                        isVisible={redistributeOpen}
                        onClose={() => setRedistributeOpen(false)}
                        subjects={openSubjectIds.map((id) => ({
                            id,
                            name:
                                subjectById.get(String(id))?.label ?? "Matière",
                        }))}
                        weeklyHours={openSelectedCount * sessionHours}
                        step={sessionHours}
                        periodLabel=" de ce week-end"
                        // La répartition COURANTE, pas un split neuf : la modale
                        // s'ouvre sur ce que l'écran vient d'annoncer, sinon elle
                        // propose autre chose que ce que le parent lit derrière.
                        initialAllocation={openHoursBySubject}
                        onSave={(allocation) => {
                            if (openStart) {
                                setHoursOverride((prev) => ({
                                    ...prev,
                                    [openStart]: allocation,
                                }))
                            }
                            setRedistributeOpen(false)
                        }}
                    />
                    <StageSubjectPickerModal
                        isVisible={!!subjectPickerSession}
                        onClose={() => setSubjectPickerSession(null)}
                        subjectOptions={subjectOptions}
                        initialSelectedIds={
                            subjectPickerSession
                                ? sessionSubjects[subjectPickerSession] || []
                                : []
                        }
                        maxSubjects={maxSubjects}
                        loading={subjectsLoading}
                        sessionLabel={
                            // En unité créneau, nommer la SESSION (« Week-end du
                            // 12-13 septembre ») et non le premier créneau : les
                            // matières valent pour tout le week-end, et titrer
                            // « Samedi 10h-12h » laisserait croire l'inverse.
                            (() => {
                                const item = offeredSessions.find(
                                    (s) =>
                                        subjectsKeyOf(s) ===
                                        subjectPickerSession
                                )
                                if (!item) return undefined
                                return isSeanceUnit
                                    ? (item.sessionLabel ?? item.label)
                                    : item.label
                            })()
                        }
                        unitLabel={isSeanceUnit ? "week-end" : "semaine"}
                        canApplyToAll={selectedSessions.length > 1}
                        onValidate={applySubjectsFromPicker}
                    />
                </>
            }
        >
            <SummerEyebrow label={`📚 ${templateName || "Stage"}`} />
            <Text style={styles.title}>
                {isSeanceUnit
                    ? "Composez votre stage"
                    : "Choisissez vos semaines"}
            </Text>

            {loadingConfig && !config ? (
                <View style={styles.configLoading}>
                    <ActivityIndicator color={theme.colors.primary} />
                </View>
            ) : configLoadError ? (
                <View>
                    <Text style={styles.emptyText}>
                        Impossible de charger les sessions du stage. Vérifiez
                        votre connexion puis réessayez.
                    </Text>
                    <FilledButtonM
                        label="Réessayer"
                        onPress={retryConfig}
                        color={theme.colors.primary}
                        style={styles.retryButton}
                    />
                </View>
            ) : catalogUnavailable ? (
                <Text style={styles.emptyText}>
                    Les inscriptions pour ce stage sont fermées pour le moment.
                    Contactez-nous pour connaître les prochaines dates.
                </Text>
            ) : (
                <>
                    {/* Sessions — une carte cochable par date de la
                        saison ; matières PAR session cochée, dans la
                        carte (la causalité session → matières se lit
                        à l'écran). */}
                    {/* Unité créneau : pas de phrase d'explication — les trois
                        étapes numérotées du compositeur la disent mieux. */}
                    {!isSeanceUnit ? (
                        <Text style={styles.hint}>
                            {weeklySeances
                                ? `Chaque semaine = ${weeklySeances} séances en petit groupe. `
                                : ""}
                            Cochez celles qui vous arrangent — elles peuvent ne
                            pas se suivre.
                        </Text>
                    ) : null}
                    {/* Unité créneau : un week-end = une carte
                        dépliable, matières avec volume horaire puis
                        créneaux. La liste de semaines ci-dessous reste
                        le rendu de l'unité semaine, inchangé. */}
                    {isSeanceUnit ? (
                        <View onLayout={onSectionLayout("composer")}>
                            <StageWeekendComposer
                                group={
                                    openGroup
                                        ? {
                                              ...openGroup,
                                              closesAt: openGroupClosesAt,
                                          }
                                        : null
                                }
                                closedGroup={closedGroup}
                                selectedKeys={selectedSessions}
                                subjectIds={openSubjectIds}
                                subjectById={subjectById}
                                subjectGoals={
                                    openStart
                                        ? (subjectGoals[openStart] ?? {})
                                        : {}
                                }
                                hoursBySubject={openHoursBySubject}
                                canAddMore={canAddMore}
                                scarcity={
                                    openGroup && scarcityShown
                                        ? stageSessionScarcity({
                                              startDate: openGroup.start,
                                              seasonFirstStart,
                                          })
                                        : null
                                }
                                onToggleSlot={toggleSession}
                                subjectOptions={subjectOptions}
                                maxSubjects={maxSubjects}
                                subjectsLoading={subjectsLoading}
                                onToggleSubject={(id) =>
                                    openStart &&
                                    toggleSubjectForSession(openStart, id)
                                }
                                onSetGoal={(subjectId, slug) =>
                                    openStart &&
                                    setGoalFor(openStart, subjectId, slug)
                                }
                                onOpenRedistribute={() =>
                                    setRedistributeOpen(true)
                                }
                                sectionToFix={sectionToFix}
                                toFixNudge={toFixNudge}
                                onSectionLayout={onSectionLayout}
                                sectionRef={sectionRef}
                            />
                        </View>
                    ) : null}
                    <View style={styles.sessionList}>
                        {(isSeanceUnit ? [] : offeredSessions).map(
                            (session) => {
                                const selected = selectedSessions.includes(
                                    session.key
                                )
                                // « Complet » (délai plancher 48h atteint) :
                                // grisée, non cochable — au même titre que le
                                // cap de count, mais avec l'état distinct.
                                const full = session.full === true
                                const locked =
                                    full || (!selected && !canAddMore)
                                const subjectsOfSession =
                                    sessionSubjects[subjectsKeyOf(session)] ||
                                    []
                                const scarcity = scarcityShown
                                    ? stageSessionScarcity({
                                          startDate: session.startDate,
                                          seasonFirstStart,
                                      })
                                    : null
                                const chosenSubjects = subjectsOfSession
                                    .map((id) => subjectById.get(id))
                                    .filter(Boolean)
                                const canAddSubject =
                                    subjectsOfSession.length < maxSubjects
                                return (
                                    <View
                                        key={session.key}
                                        style={[
                                            styles.sessionCard,
                                            selected &&
                                                styles.sessionCardSelected,
                                            locked && styles.sessionCardLocked,
                                        ]}
                                    >
                                        <TouchableOpacity
                                            activeOpacity={0.8}
                                            disabled={locked}
                                            onPress={() =>
                                                toggleSession(session.key)
                                            }
                                            style={styles.sessionHeader}
                                        >
                                            <FontAwesome5
                                                name={
                                                    selected
                                                        ? "check-square"
                                                        : "square"
                                                }
                                                solid={selected}
                                                size={18}
                                                color={
                                                    selected
                                                        ? theme.colors.primary
                                                        : theme.colors.lightMore
                                                }
                                                style={styles.sessionCheckbox}
                                            />
                                            <View
                                                style={
                                                    styles.sessionHeaderTexts
                                                }
                                            >
                                                <Text
                                                    style={[
                                                        styles.sessionLabel,
                                                        selected &&
                                                            styles.sessionLabelSelected,
                                                        locked &&
                                                            styles.sessionLabelLocked,
                                                    ]}
                                                >
                                                    {session.label}
                                                </Text>
                                                {/* Rareté FOMO — même chiffre
                                                déterministe que la LP, sur
                                                chaque carte (visible avant
                                                même de cocher). Une semaine
                                                « Complet » affiche l'état
                                                distinct à la place. */}
                                                {scarcity || full ? (
                                                    <StageScarcityMeter
                                                        places={
                                                            scarcity?.places
                                                        }
                                                        gaugeFill={
                                                            scarcity?.gaugeFill
                                                        }
                                                        full={full}
                                                    />
                                                ) : null}
                                            </View>
                                        </TouchableOpacity>

                                        {selected && (
                                            <View
                                                style={styles.sessionSubjects}
                                            >
                                                <Text
                                                    style={
                                                        styles.sessionSubjectsHint
                                                    }
                                                >
                                                    {subjectsHint(
                                                        maxSubjects,
                                                        subjectsOfSession.length
                                                    )}
                                                </Text>
                                                {subjectsLoading ? (
                                                    <View
                                                        style={
                                                            styles.subjectsLoading
                                                        }
                                                    >
                                                        <ActivityIndicator
                                                            color={
                                                                theme.colors
                                                                    .primary
                                                            }
                                                        />
                                                    </View>
                                                ) : availableSubjects.length ===
                                                  0 ? (
                                                    <Text
                                                        style={styles.emptyText}
                                                    >
                                                        Aucune matière
                                                        disponible pour cette
                                                        classe pour le moment.
                                                    </Text>
                                                ) : (
                                                    <>
                                                        {/* Résumé compact des
                                                        matières choisies :
                                                        petites chips avec ×.
                                                        Le « mur de chips »
                                                        vit désormais dans la
                                                        modale. */}
                                                        {chosenSubjects.length ? (
                                                            <View
                                                                style={
                                                                    styles.chosenChips
                                                                }
                                                            >
                                                                {chosenSubjects.map(
                                                                    (opt) => (
                                                                        <View
                                                                            key={
                                                                                opt.id
                                                                            }
                                                                            style={
                                                                                styles.chosenChip
                                                                            }
                                                                        >
                                                                            {opt.iconUri ? (
                                                                                <Image
                                                                                    source={{
                                                                                        uri: opt.iconUri,
                                                                                    }}
                                                                                    style={
                                                                                        styles.chosenChipIcon
                                                                                    }
                                                                                    resizeMode="contain"
                                                                                />
                                                                            ) : null}
                                                                            <Text
                                                                                style={
                                                                                    styles.chosenChipLabel
                                                                                }
                                                                            >
                                                                                {
                                                                                    opt.label
                                                                                }
                                                                            </Text>
                                                                            <TouchableOpacity
                                                                                onPress={() =>
                                                                                    toggleSubjectForSession(
                                                                                        subjectsKeyOf(
                                                                                            session
                                                                                        ),
                                                                                        opt.id
                                                                                    )
                                                                                }
                                                                                hitSlop={{
                                                                                    top: 8,
                                                                                    bottom: 8,
                                                                                    left: 6,
                                                                                    right: 8,
                                                                                }}
                                                                                style={
                                                                                    styles.chosenChipRemove
                                                                                }
                                                                            >
                                                                                <FontAwesome5
                                                                                    name="times"
                                                                                    size={
                                                                                        11
                                                                                    }
                                                                                    color={
                                                                                        theme
                                                                                            .colors
                                                                                            .primary
                                                                                    }
                                                                                />
                                                                            </TouchableOpacity>
                                                                        </View>
                                                                    )
                                                                )}
                                                            </View>
                                                        ) : null}
                                                        {canAddSubject ? (
                                                            <TouchableOpacity
                                                                activeOpacity={
                                                                    0.8
                                                                }
                                                                onPress={() =>
                                                                    setSubjectPickerSession(
                                                                        subjectsKeyOf(
                                                                            session
                                                                        )
                                                                    )
                                                                }
                                                                style={
                                                                    styles.addSubjectButton
                                                                }
                                                            >
                                                                <FontAwesome5
                                                                    name="plus"
                                                                    size={11}
                                                                    color={
                                                                        theme
                                                                            .colors
                                                                            .primary
                                                                    }
                                                                />
                                                                <Text
                                                                    style={
                                                                        styles.addSubjectText
                                                                    }
                                                                >
                                                                    {chosenSubjects.length
                                                                        ? "Ajouter une matière"
                                                                        : "Choisir la matière"}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        ) : null}
                                                    </>
                                                )}
                                            </View>
                                        )}
                                    </View>
                                )
                            }
                        )}
                    </View>

                    {/* Counts non couverts par une variante en vente :
                        on l'explique (façon bannière aide) au lieu de
                        laisser cocher puis échouer au checkout. */}
                    {!canAddMore && hasUncheckedLeft && (
                        <Pressable
                            onPress={() =>
                                Linking.openURL(SUPPORT_PHONE_URL).catch(
                                    () => {}
                                )
                            }
                            accessibilityRole="link"
                            style={styles.morePill}
                        >
                            <FontAwesome5
                                name="phone-alt"
                                solid
                                size={11}
                                color={theme.colors.primary}
                            />
                            <Text style={styles.morePillText}>
                                {count + 1}{" "}
                                {isSeanceUnit ? "créneaux" : "semaines"} et + :
                                nous contacter au{" "}
                                <Text style={styles.morePillPhone}>
                                    {SUPPORT_PHONE_DISPLAY}
                                </Text>
                            </Text>
                        </Pressable>
                    )}

                    {error === "no_sessions" && (
                        <Text style={styles.errorText}>
                            {isSeanceUnit
                                ? "Cochez au moins un créneau."
                                : "Cochez au moins une semaine."}
                        </Text>
                    )}
                    {error === "missing_subjects" && (
                        <Text style={styles.errorText}>
                            {isSeanceUnit
                                ? "Choisissez au moins une matière pour chaque week-end."
                                : "Choisissez au moins une matière pour chaque semaine cochée."}
                        </Text>
                    )}
                    {error === "count_not_sellable" && (
                        <Text style={styles.errorText}>
                            {isSeanceUnit
                                ? "Ce nombre de créneaux n'est pas proposé pour le moment — ajustez votre sélection ou contactez-nous."
                                : "Ce nombre de semaines n'est pas proposé pour le moment — ajustez votre sélection ou contactez-nous."}
                        </Text>
                    )}

                    {/* Horaire — visible UNIQUEMENT si le template
                        offre un vrai choix (> 1 horaire) ; sinon
                        l'unique horaire est auto-sélectionné en
                        silence. */}
                    {slots.length > 1 && (
                        <>
                            <Text
                                style={[styles.sectionTitle, { marginTop: 24 }]}
                            >
                                Horaire
                            </Text>
                            <Text style={styles.hint}>
                                Le même horaire s'applique à toutes les semaines
                                cochées.
                            </Text>
                            <View style={styles.slotList}>
                                {slots.map((s) => {
                                    const selected = s.key === slotKey
                                    return (
                                        <TouchableOpacity
                                            key={s.key}
                                            activeOpacity={0.8}
                                            onPress={() => {
                                                setError(null)
                                                setSlotKey(s.key)
                                            }}
                                            style={[
                                                styles.slotCard,
                                                selected &&
                                                    styles.slotCardSelected,
                                            ]}
                                        >
                                            <FontAwesome5
                                                name={
                                                    selected
                                                        ? "check-circle"
                                                        : "circle"
                                                }
                                                solid={selected}
                                                size={14}
                                                color={
                                                    selected
                                                        ? theme.colors.primary
                                                        : theme.colors.lightMore
                                                }
                                            />
                                            <View style={styles.slotTexts}>
                                                {s.label ? (
                                                    <Text
                                                        style={[
                                                            styles.slotLabel,
                                                            selected &&
                                                                styles.slotLabelSelected,
                                                        ]}
                                                    >
                                                        {s.label}
                                                    </Text>
                                                ) : null}
                                                {s.sessionLabels.length ? (
                                                    <Text
                                                        style={
                                                            s.label
                                                                ? styles.slotSub
                                                                : [
                                                                      styles.slotLabel,
                                                                      selected &&
                                                                          styles.slotLabelSelected,
                                                                  ]
                                                        }
                                                    >
                                                        {s.sessionLabels.join(
                                                            " · "
                                                        )}
                                                    </Text>
                                                ) : null}
                                            </View>
                                        </TouchableOpacity>
                                    )
                                })}
                            </View>
                        </>
                    )}

                    {/* Tarif — le prix se construit sous les yeux du
                        parent : « dès X € » tant que rien n'est coché,
                        puis le total catalogue du count coché. */}
                    <View style={styles.priceCard}>
                        <Text style={styles.priceLabel}>Total du stage</Text>
                        {count === 0 ? (
                            <>
                                {minPricingCents != null ? (
                                    <Text style={styles.priceValue}>
                                        dès {formatEuros(minPricingCents / 100)}
                                    </Text>
                                ) : null}
                                <Text style={styles.priceNote}>
                                    {isSeanceUnit
                                        ? "Cochez vos créneaux — le prix suit votre sélection."
                                        : "Cochez vos semaines — le prix suit votre sélection."}
                                </Text>
                            </>
                        ) : (
                            <>
                                {/* Prix de référence barré + remise :
                                    la promo doit se lire ICI, pas
                                    seulement sur la landing — c'est ce
                                    qui justifiera le tarif régulier au
                                    moment de la conversion. Le
                                    pourcentage est CALCULÉ des deux
                                    montants, jamais servi. */}
                                <View style={styles.priceRow}>
                                    {discount != null ? (
                                        <Text style={styles.priceCompare}>
                                            {formatEuros(
                                                pricing.compareAtCents / 100
                                            )}
                                        </Text>
                                    ) : null}
                                    <Text style={styles.priceValue}>
                                        {formatEuros(
                                            (pricing?.priceCents ?? 0) / 100
                                        )}
                                    </Text>
                                </View>
                                {discount != null ? (
                                    <View style={styles.discountPill}>
                                        <Text style={styles.discountText}>
                                            −{discount} %
                                        </Text>
                                    </View>
                                ) : null}
                                <Text style={styles.pricePerSession}>
                                    {isSeanceUnit
                                        ? `${count * sessionHours} h`
                                        : `${count} semaine${count > 1 ? "s" : ""}`}
                                    {perHourCents != null
                                        ? ` · soit ${formatEuros(
                                              perHourCents / 100
                                          )}/heure`
                                        : ""}
                                </Text>
                                <Text style={styles.priceNote}>
                                    {formatEuros(
                                        getPreinscriptionCents(config) / 100
                                    )}{" "}
                                    aujourd'hui pour réserver · solde prélevé au
                                    démarrage du stage.
                                </Text>
                                <View style={styles.priceSecureRow}>
                                    <FontAwesome5
                                        name="lock"
                                        size={11}
                                        color={theme.colors.success}
                                    />
                                    <Text style={styles.priceSecureText}>
                                        Paiement sécurisé à l'étape suivante
                                    </Text>
                                </View>
                            </>
                        )}
                    </View>
                </>
            )}
            <SummerHelpBanner
                track={track}
                step={3}
                trackingEvent="stage_funnel_help_clicked"
            />
        </FunnelStepLayout>
    )
}

/**
 * La répartition manuelle d'une session, ou `null` si elle ne vaut plus.
 *
 * L'état du funnel enregistre `session_subject_hours` — le contrat backend —
 * qu'elle vienne du parent ou de la répartition automatique. En la relisant
 * telle quelle, une répartition calculée pour UN créneau survivait à l'ajout
 * d'un second : la matière servie à zéro heure le restait, et la ligne de
 * répartition, qui masque les zéros, ne la montrait même plus.
 *
 * Une répartition ne vaut donc que si elle couvre EXACTEMENT les matières
 * retenues et le volume acheté. Sinon on repart de l'automatique, qui est juste
 * par construction.
 */
function manualSplitFor(split, subjectIds, totalHours) {
    if (!split || !Object.keys(split).length) return null
    const wanted = subjectIds.map(String).sort()
    const got = Object.keys(split).sort()
    if (wanted.length !== got.length) return null
    if (wanted.some((id, i) => id !== got[i])) return null
    const sum = Object.values(split).reduce((acc, h) => acc + Number(h || 0), 0)
    return sum === totalHours ? split : null
}

// { date: [ids] } (int backend / string chips) → { date: [ids en STRING] }.
function normalizeSessionSubjects(raw) {
    if (!raw || typeof raw !== "object") return {}
    return Object.fromEntries(
        Object.entries(raw).map(([date, ids]) => [
            date,
            (Array.isArray(ids) ? ids : []).map(String),
        ])
    )
}

// Libellé matières d'une session cochée — le cap vient du backend (classe) :
// CE2→5ème = 2 (la séance de 2h se partage 1h + 1h), sinon 1.
function subjectsHint(maxSubjects, selectedCount) {
    if (maxSubjects <= 1) return "La matière de cette semaine :"
    if (selectedCount >= 2) return "2 matières — 1h + 1h chaque jour."
    return "Jusqu'à 2 matières (1h + 1h chaque jour) :"
}

const styles = StyleSheet.create({
    content: { paddingBottom: 24 },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 10,
    },
    sectionTitle: {
        fontFamily: "Montserrat-Bold",
        fontSize: 16,
        color: theme.colors.primary,
        marginBottom: 4,
    },
    hint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        marginBottom: 10,
        lineHeight: 16,
    },
    configLoading: { paddingVertical: 48, alignItems: "center" },
    retryButton: { width: "100%", marginTop: 8, marginBottom: 0 },
    subjectsLoading: { paddingVertical: 16, alignItems: "center" },
    emptyText: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        paddingVertical: 12,
        lineHeight: 18,
    },
    errorText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.error,
        marginTop: 8,
    },

    // Sessions : cartes cochables pleine largeur ; les matières de la session
    // se déplient DANS la carte cochée.
    sessionList: { gap: 8 },
    sessionCard: {
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        borderRadius: 12,
        backgroundColor: theme.colors.white,
    },
    sessionCardSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    sessionCardLocked: { opacity: 0.55 },
    sessionHeader: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10,
        paddingVertical: 12,
        paddingHorizontal: 14,
    },
    // Nudge la case à cocher sur la 1ʳᵉ ligne de texte (le bloc titre + jauge
    // fait deux lignes).
    sessionCheckbox: { marginTop: 1 },
    sessionHeaderTexts: { flex: 1 },
    sessionLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.grey,
    },
    sessionLabelSelected: { color: theme.colors.primary },
    sessionLabelLocked: { color: theme.colors.lightMore },
    sessionSubjects: {
        paddingHorizontal: 14,
        paddingBottom: 12,
    },
    sessionSubjectsHint: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
        marginBottom: 8,
        lineHeight: 15,
    },

    // Résumé compact des matières choisies (chips avec ×) + bouton « Ajouter »
    // — remplace le mur de chips inline, la sélection réelle vit dans la modale.
    chosenChips: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        marginBottom: 8,
    },
    chosenChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 14,
        borderWidth: 1.5,
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.white,
    },
    chosenChipIcon: { width: 16, height: 16 },
    chosenChipLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.primary,
    },
    chosenChipRemove: { marginLeft: 1 },
    addSubjectButton: {
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        gap: 7,
        minHeight: 34,
        paddingVertical: 7,
        paddingHorizontal: 12,
        borderRadius: 10,
        borderWidth: 1.5,
        borderStyle: "dashed",
        borderColor: theme.colors.primaryLight,
        backgroundColor: theme.colors.white,
    },
    addSubjectText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.primary,
    },

    // « N sessions et + : nous contacter » — même langage que la bannière aide.
    morePill: {
        marginTop: 10,
        minHeight: 36,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 10,
        backgroundColor: theme.colors.primaryLighter,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
    },
    morePillText: {
        fontFamily: "Montserrat-Medium",
        fontSize: 12,
        color: theme.colors.primary,
        flexShrink: 1,
    },
    morePillPhone: {
        fontFamily: "Montserrat-ExtraBold",
        textDecorationLine: "underline",
    },

    // Horaires : lignes radio.
    slotList: { gap: 8 },
    slotCard: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        borderRadius: 12,
        paddingVertical: 10,
        paddingHorizontal: 12,
        backgroundColor: theme.colors.white,
    },
    slotCardSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    slotTexts: { flex: 1 },
    slotLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.grey,
        flexShrink: 1,
    },
    slotLabelSelected: { color: theme.colors.primary },
    slotSub: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
        marginTop: 2,
    },

    // Bloc tarif.
    priceCard: {
        alignItems: "center",
        marginTop: 24,
        padding: 16,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.whiteAlmost,
    },
    priceLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.grey,
    },
    priceRow: { flexDirection: "row", alignItems: "baseline", gap: 10 },
    // Aligné sur le barré du funnel brevet (`summaryStrike`) : discret, il ne
    // concurrence pas le prix de lancement qu'il sert à mettre en valeur.
    priceCompare: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.light,
        textDecorationLine: "line-through",
    },
    priceValue: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 32,
        lineHeight: 38,
        color: theme.colors.primary,
    },
    discountPill: {
        marginTop: 4,
        paddingVertical: 3,
        paddingHorizontal: 10,
        borderRadius: 10,
        backgroundColor: theme.colors.error,
    },
    discountText: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 12,
        color: theme.colors.white,
    },
    pricePerSession: {
        fontFamily: "Montserrat-Medium",
        fontSize: 11,
        color: theme.colors.grey,
        marginTop: 1,
    },
    priceNote: {
        fontFamily: "Montserrat-Regular",
        fontSize: 11,
        color: theme.colors.grey,
        marginTop: 4,
        lineHeight: 16,
        textAlign: "center",
    },
    priceSecureRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        marginTop: 8,
    },
    priceSecureText: {
        fontFamily: "Montserrat-SemiBold",
        fontSize: 11,
        color: theme.colors.success,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    footerRecap: {
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "space-between",
        marginBottom: 10,
    },
    footerRecapLine: {
        fontFamily: "Montserrat-Medium",
        fontSize: 13,
        color: theme.colors.grey,
    },
    footerPriceRow: { flexDirection: "row", alignItems: "baseline", gap: 7 },
    footerCompare: {
        fontFamily: "Montserrat-Medium",
        fontSize: 13,
        color: theme.colors.light,
        textDecorationLine: "line-through",
    },
    footerPrice: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 19,
        color: theme.colors.primaryDark || theme.colors.primary,
        letterSpacing: -0.3,
    },
})

const mapStateToProps = (state) => ({
    reduxFunnelStateId: state.data?.stageFunnel?.funnelStateId ?? null,
})

const mapDispatchToProps = { apiRequest }

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(StageConfiguratorScreen)
